import { useRef, useEffect } from 'react';
import { Bold, Italic, Underline, List, ListOrdered } from 'lucide-react';

/**
 * Tiny contentEditable-based rich text editor.
 *
 * Supports bold / italic / underline and ordered/unordered lists via the
 * browser's ``document.execCommand``. Emits HTML via ``onChange(html)``.
 *
 * Designed for short email bodies where we don't need a heavy editor like
 * Quill (which currently doesn't work under React 19).
 */
export default function RichTextArea({ value, onChange, className = '', testId }) {
  const editorRef = useRef(null);

  // Sync external value into the DOM when it changes and diverges (e.g.
  // switching competition re-loads the template). Avoids cursor jumps in
  // the common case where value already matches the DOM.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if ((value || '') !== el.innerHTML) {
      el.innerHTML = value || '';
    }
  }, [value]);

  const exec = (cmd) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, null);
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const onInput = () => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const ToolbarButton = ({ icon: Icon, label, cmd }) => (
    <button
      type="button"
      aria-label={label}
      title={label}
      onMouseDown={(e) => { e.preventDefault(); exec(cmd); }}
      className="p-1.5 rounded hover:bg-stone-200 text-stone-700"
    >
      <Icon className="h-4 w-4" />
    </button>
  );

  return (
    <div className={`border rounded-lg overflow-hidden ${className}`}>
      <div className="flex items-center gap-1 border-b bg-stone-50 px-2 py-1">
        <ToolbarButton icon={Bold} label="Bold" cmd="bold" />
        <ToolbarButton icon={Italic} label="Italic" cmd="italic" />
        <ToolbarButton icon={Underline} label="Underline" cmd="underline" />
        <span className="mx-1 h-4 w-px bg-stone-300" />
        <ToolbarButton icon={List} label="Bulleted list" cmd="insertUnorderedList" />
        <ToolbarButton icon={ListOrdered} label="Numbered list" cmd="insertOrderedList" />
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={onInput}
        data-testid={testId}
        className="min-h-[160px] px-3 py-2 text-sm focus:outline-none"
      />
    </div>
  );
}
